import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  CheckCircle2, 
  Circle, 
  Clock, 
  AlertCircle,
  Plus,
  Filter
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

const PRIORITY_COLORS = {
  low: 'bg-gray-100 text-gray-700',
  medium: 'bg-blue-100 text-blue-700',
  high: 'bg-orange-100 text-orange-700',
  urgent: 'bg-red-100 text-red-700'
};

const STATUS_ICONS = {
  pending: Circle,
  in_progress: Clock,
  waiting: AlertCircle,
  completed: CheckCircle2,
  cancelled: Circle
};

export default function Tasks() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  
  // New task form
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    priority: 'medium',
    due_date: '',
    assignee_name: 'Admin'
  });

  const loadTasks = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/tasks`, { withCredentials: true });
      setTasks(response.data);
    } catch (error) {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  useEffect(() => {
    if (filterStatus === 'all') {
      setFilteredTasks(tasks);
    } else {
      setFilteredTasks(tasks.filter(t => t.status === filterStatus));
    }
  }, [tasks, filterStatus]);

  const createTask = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/tasks`, newTask, { withCredentials: true });
      toast.success('Task created');
      setCreateModalOpen(false);
      setNewTask({
        title: '',
        description: '',
        priority: 'medium',
        due_date: '',
        assignee_name: 'Admin'
      });
      loadTasks();
    } catch (error) {
      toast.error('Failed to create task');
    }
  };

  const updateTaskStatus = async (taskId, newStatus) => {
    try {
      await axios.patch(`${API}/tasks/${taskId}`, { status: newStatus }, { withCredentials: true });
      toast.success('Task updated');
      loadTasks();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const pendingCount = tasks.filter(t => t.status === 'pending').length;
  const inProgressCount = tasks.filter(t => t.status === 'in_progress').length;
  const completedCount = tasks.filter(t => t.status === 'completed').length;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F8F8] flex items-center justify-center">
        <p className="text-[#474747]">Loading tasks...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F8F8]">
      {/* Header */}
      <header className="bg-white border-b border-[#E9E9E9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h1 className="text-2xl sm:text-3xl font-heading font-semibold text-[#474747]">Tasks</h1>
              <p className="text-sm text-gray-500 mt-1">{tasks.length} total tasks</p>
            </div>
            <Button
              onClick={() => setCreateModalOpen(true)}
              className="bg-[#C6E76C] hover:bg-[#b5d65f] text-[#474747] font-semibold shrink-0"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Task
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 sm:py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <div className="bg-white rounded-lg border border-[#E9E9E9] p-4 sm:p-6">
            <p className="text-xs sm:text-sm text-gray-500 font-medium">Pending</p>
            <p className="text-2xl sm:text-3xl font-semibold text-[#474747] mt-1 sm:mt-2">{pendingCount}</p>
          </div>
          <div className="bg-white rounded-lg border border-[#E9E9E9] p-4 sm:p-6">
            <p className="text-xs sm:text-sm text-gray-500 font-medium">In Progress</p>
            <p className="text-2xl sm:text-3xl font-semibold text-[#474747] mt-1 sm:mt-2">{inProgressCount}</p>
          </div>
          <div className="bg-white rounded-lg border border-[#E9E9E9] p-4 sm:p-6">
            <p className="text-xs sm:text-sm text-gray-500 font-medium">Completed</p>
            <p className="text-2xl sm:text-3xl font-semibold text-[#8CA43C] mt-1 sm:mt-2">{completedCount}</p>
          </div>
          <div className="bg-white rounded-lg border border-[#E9E9E9] p-4 sm:p-6">
            <p className="text-xs sm:text-sm text-gray-500 font-medium">Total</p>
            <p className="text-2xl sm:text-3xl font-semibold text-[#474747] mt-1 sm:mt-2">{tasks.length}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-6">
          {['all', 'pending', 'in_progress', 'waiting', 'completed'].map(status => (
            <button
              key={status}
              onClick={() => setFilterStatus(status)}
              className={`px-3 py-1.5 sm:px-4 sm:py-2 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                filterStatus === status
                  ? 'bg-[#C6E76C] text-[#474747]'
                  : 'bg-white text-gray-600 hover:bg-gray-50 border border-[#E9E9E9]'
              }`}
            >
              {status.replace('_', ' ').charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
            </button>
          ))}
        </div>

        {/* Task List */}
        <div className="space-y-3">
          {filteredTasks.length === 0 ? (
            <div className="bg-white rounded-lg border border-[#E9E9E9] p-12 text-center">
              <p className="text-gray-500">No tasks found</p>
            </div>
          ) : (
            filteredTasks.map(task => {
              const StatusIcon = STATUS_ICONS[task.status] || Circle;
              return (
                <div
                  key={task.id}
                  className="bg-white rounded-lg border border-[#E9E9E9] p-4 sm:p-6 hover:shadow-sm transition-shadow"
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="flex items-start gap-3 sm:gap-4 flex-1 min-w-0">
                      <button
                        onClick={() => {
                          const newStatus = task.status === 'completed' ? 'pending' : 'completed';
                          updateTaskStatus(task.id, newStatus);
                        }}
                        className="mt-1 shrink-0"
                      >
                        <StatusIcon className={`w-5 h-5 ${task.status === 'completed' ? 'text-[#8CA43C]' : 'text-gray-400'}`} />
                      </button>
                      <div className="flex-1 min-w-0">
                        <h3 className={`text-base sm:text-lg font-semibold leading-snug ${task.status === 'completed' ? 'line-through text-gray-400' : 'text-[#474747]'}`}>
                          {task.title}
                        </h3>
                        {task.description && (
                          <p className="text-sm text-gray-600 mt-1">{task.description}</p>
                        )}
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 sm:mt-3">
                          {task.due_date && (
                            <span className="text-xs text-gray-500">
                              Due: {new Date(task.due_date).toLocaleDateString('en-AU')}
                            </span>
                          )}
                          {task.assignee_name && (
                            <span className="text-xs text-gray-500">
                              Assigned to: {task.assignee_name}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 pl-8 sm:pl-0 shrink-0">
                      <span className={`px-2.5 sm:px-3 py-1 rounded-full text-xs font-medium ${PRIORITY_COLORS[task.priority]}`}>
                        {task.priority}
                      </span>
                      <select
                        value={task.status}
                        onChange={(e) => updateTaskStatus(task.id, e.target.value)}
                        className="text-sm border border-[#E9E9E9] rounded-md px-2 sm:px-3 py-1 bg-white"
                      >
                        <option value="pending">Pending</option>
                        <option value="in_progress">In Progress</option>
                        <option value="waiting">Waiting</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Create Task Modal */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading font-semibold text-[#474747]">Create New Task</DialogTitle>
          </DialogHeader>
          <form onSubmit={createTask} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-[#474747] block mb-2">Title *</label>
              <input
                type="text"
                required
                value={newTask.title}
                onChange={(e) => setNewTask({...newTask, title: e.target.value})}
                className="w-full p-2 border border-[#E9E9E9] rounded-md"
                placeholder="e.g., Vet new application"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#474747] block mb-2">Description</label>
              <textarea
                value={newTask.description}
                onChange={(e) => setNewTask({...newTask, description: e.target.value})}
                className="w-full p-2 border border-[#E9E9E9] rounded-md"
                rows="3"
                placeholder="Additional details..."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-[#474747] block mb-2">Priority</label>
                <select
                  value={newTask.priority}
                  onChange={(e) => setNewTask({...newTask, priority: e.target.value})}
                  className="w-full p-2 border border-[#E9E9E9] rounded-md"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-[#474747] block mb-2">Due Date</label>
                <input
                  type="date"
                  value={newTask.due_date}
                  onChange={(e) => setNewTask({...newTask, due_date: e.target.value})}
                  className="w-full p-2 border border-[#E9E9E9] rounded-md"
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="submit" className="flex-1 bg-[#C6E76C] hover:bg-[#b5d65f] text-[#474747] font-semibold">
                Create Task
              </Button>
              <Button
                type="button"
                onClick={() => setCreateModalOpen(false)}
                variant="outline"
                className="border-[#E9E9E9]"
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
